import React, { useEffect, useMemo, useState } from 'react';
import { PageLayout } from '../../../components/common/PageLayout';
import { useDeficitPeriods, useManagedDeficits } from '../../../hooks/useDeficitsApi';
import { deficitsNav } from '../nav';

function formatPeriodLabel(period: { LowerDate: string; UpperDate: string }): string {
  return `${period.LowerDate} - ${period.UpperDate}`;
}

function formatMoney(value: number): string {
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export const DeficitsReportPage: React.FC = () => {
  const [selectedPeriodId, setSelectedPeriodId] = useState<number | null>(null);
  const { data: periods = [], isLoading: periodsLoading } = useDeficitPeriods();
  const { data: manageReport, isLoading: manageLoading } = useManagedDeficits(selectedPeriodId, true);

  useEffect(() => {
    document.title = 'Deficits Report — Corp Admin';
  }, []);

  useEffect(() => {
    if (periods.length > 0 && selectedPeriodId === null) {
      setSelectedPeriodId(periods[0].PeriodId);
    }
  }, [periods, selectedPeriodId]);

  const manageRows = manageReport?.Rows ?? [];

  const manageDeficitsActorLabel = useMemo(() => {
    if (manageLoading) return 'Loading…';
    if (!manageReport) return '…';
    const name = manageReport.DeficitsProcessedByName?.trim();
    const email = manageReport.DeficitsProcessedByEmail?.trim();
    if (!name && !email) return 'Not recorded for this period';
    if (name && email) return `${name} (${email})`;
    return name ?? email ?? '—';
  }, [manageLoading, manageReport]);

  return (
    <PageLayout navItems={deficitsNav}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-primary">Deficits Report</h1>
        <p className="mt-1 text-sm text-muted">
          View saved draw carryover by payroll period (read-only).
        </p>
      </div>

      <section className="mb-6 rounded-lg border border-border-default bg-surface-alt p-4">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-wrap items-end gap-8">
            <div>
              <p className="text-xs font-medium uppercase text-muted">Payroll Period</p>
              <select
                className="mt-1 rounded-md border border-border-default bg-surface px-3 py-2 text-sm text-primary"
                value={selectedPeriodId ?? ''}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  setSelectedPeriodId(Number.isNaN(value) ? null : value);
                }}
              >
                {periodsLoading && <option value="">Loading periods...</option>}
                {!periodsLoading && periods.length === 0 && <option value="">No periods found</option>}
                {periods.map((period) => (
                  <option key={period.PeriodId} value={period.PeriodId}>
                    {formatPeriodLabel(period)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <p className="text-xs font-medium uppercase text-muted">Total Deficits</p>
              <p className="mt-1 text-sm font-medium text-primary">
                {manageLoading || !manageReport ? '…' : formatMoney(manageReport.TotalDrawCarryover ?? 0)}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs font-medium uppercase text-muted">Deficits Processed By</p>
            <p className="mt-1 text-sm text-primary">{manageDeficitsActorLabel}</p>
          </div>
        </div>
      </section>

      {manageLoading ? (
        <p className="text-sm text-muted">Loading report…</p>
      ) : manageRows.length === 0 ? (
        <p className="text-sm text-muted">No saved deficits for this payroll period.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border-default">
          <table className="w-full text-sm text-primary">
            <thead className="bg-surface-hover text-left text-xs uppercase text-muted">
              <tr>
                <th className="px-4 py-3">Last Name</th>
                <th className="px-4 py-3">First Name</th>
                <th className="px-4 py-3">ADP ID</th>
                <th className="px-4 py-3">Draw Carryover</th>
              </tr>
            </thead>
            <tbody>
              {manageRows.map((row) => (
                <tr key={row.TimesheetId} className="border-t border-border-default">
                  <td className="px-4 py-3">{row.LastName}</td>
                  <td className="px-4 py-3">{row.FirstName}</td>
                  <td className="px-4 py-3">{row.AdpId}</td>
                  <td className="px-4 py-3">{row.DrawCarryover.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PageLayout>
  );
};
